import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  PaintBrushBroad, PlugsConnected, UsersThree, CheckCircle,
  ArrowRight, FacebookLogo, Megaphone, ShieldCheck, UploadSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/BrandMark";

const STEPS = [
  { key: "brand", label: "Brand Kit", icon: PaintBrushBroad },
  { key: "meta", label: "Connect Meta", icon: PlugsConnected },
  { key: "team", label: "Meet the Team", icon: UsersThree },
];

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [step, setStep] = useState(() => {
    const s = parseInt(params.get("step") || "1", 10);
    return s >= 1 && s <= 3 ? s : 1;
  });
  const [busy, setBusy] = useState(false);

  const [brand, setBrand] = useState({ brand_name: "", tagline: "", logo_b64: "", colors: ["#FF6B35", "#2D2422"] });
  const [oauthCfg, setOauthCfg] = useState(null);
  const [metaStatus, setMetaStatus] = useState(null);

  useEffect(() => {
    if (user?.workspace_name) setBrand((b) => ({ ...b, brand_name: b.brand_name || user.workspace_name }));
  }, [user]);

  const loadMeta = useCallback(() => {
    api.get("/meta/oauth/config").then(({ data }) => setOauthCfg(data)).catch(() => setOauthCfg({ available: false }));
    api.get("/meta/status").then(({ data }) => setMetaStatus(data)).catch(() => {});
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  useEffect(() => {
    api.get("/onboarding").then(({ data }) => {
      if (data.completed) navigate("/", { replace: true });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const meta = params.get("meta");
    if (!meta) return;
    if (meta === "connected") toast.success("Facebook connected! Your ad accounts are linked.");
    else if (meta === "cancelled") toast.warning("Facebook connection was cancelled — you can retry or skip.");
    else toast.error(params.get("reason") || "Facebook connection failed", { duration: 9000 });
    setParams({}, { replace: true });
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Logo must be under 4MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setBrand((b) => ({ ...b, logo_b64: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveBrand = async () => {
    setBusy(true);
    try {
      const payload = { brand_name: brand.brand_name, colors: brand.colors };
      if (brand.tagline) payload.tagline = brand.tagline;
      if (brand.logo_b64) payload.logo_b64 = brand.logo_b64;
      await api.put("/team/brand", payload);
      toast.success("Brand Kit saved — your AI designer will use it on every creative");
      setStep(2);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const startOauth = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/meta/oauth/start", { params: { return_to: "/onboarding?step=2" } });
      window.location.assign(data.authorization_url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const finish = async (skipped = false) => {
    setBusy(true);
    try {
      await api.post("/onboarding/complete");
      if (!skipped) toast.success("Team initialized! Your AI teammates are ready.");
      navigate("/");
    } catch (e) {
      navigate("/");
    }
  };

  const connected = metaStatus?.connected;

  return (
    <div className="min-h-screen bg-[#FAF9F6] font-figtree" data-testid="onboarding-page">
      <header className="border-b border-[#F0EBE1] bg-white">
        <div className="max-w-2xl mx-auto flex items-center justify-between px-6 h-16">
          <BrandMark size={28} />
          <button onClick={() => finish(true)} data-testid="onboarding-skip-all"
            className="text-xs font-bold text-[#b3a99e] hover:text-[#2D2422] transition-colors">
            Skip setup →
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-10" data-testid="onboarding-progress">
          {STEPS.map((s, i) => {
            const n = i + 1;
            const active = n === step, done = n < step;
            return (
              <div key={s.key} className="flex items-center gap-3 flex-1">
                <div className={`flex items-center gap-2 ${active || done ? "" : "opacity-40"}`}>
                  <span className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border ${
                    done ? "bg-[#2D2422] border-[#2D2422] text-white" : active ? "bg-[#FF6B35] border-[#FF6B35] text-white" : "bg-white border-[#F0EBE1] text-[#8a7f76]"}`}>
                    {done ? <CheckCircle size={16} weight="fill" /> : n}
                  </span>
                  <span className={`text-xs font-bold hidden sm:block ${active ? "text-[#2D2422]" : "text-[#b3a99e]"}`}>{s.label}</span>
                </div>
                {n < 3 && <div className={`h-1 rounded-full flex-1 ${done ? "bg-[#FF6B35]" : "bg-[#FFD8C4]/60"}`} />}
              </div>
            );
          })}
        </div>

        <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
          {step === 1 && (
            <div className="bg-white border border-[#F0EBE1] rounded-3xl p-8 shadow-[0_8px_30px_rgba(255,107,53,0.06)]" data-testid="onboarding-step-brand">
              <span className="h-12 w-12 rounded-2xl bg-[#FFD8C4]/50 flex items-center justify-center">
                <PaintBrushBroad size={22} weight="duotone" className="text-[#FF6B35]" />
              </span>
              <h1 className="font-outfit text-2xl font-extrabold tracking-tight text-[#2D2422] mt-5">Set up your Brand Kit</h1>
              <p className="text-sm text-[#8a7f76] mt-2 leading-relaxed">
                Your AI designer uses this on every creative — your real logo is stamped programmatically, never redrawn by AI.
              </p>
              <div className="mt-8 space-y-5">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#8a7f76]">Brand name</Label>
                  <Input data-testid="onboarding-brand-name" value={brand.brand_name}
                    onChange={(e) => setBrand({ ...brand, brand_name: e.target.value })}
                    placeholder="Acme Institute" className="h-11 rounded-xl border-[#E5DCD0] focus-visible:ring-[#FF6B35]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#8a7f76]">Tagline (optional)</Label>
                  <Input data-testid="onboarding-brand-tagline" value={brand.tagline}
                    onChange={(e) => setBrand({ ...brand, tagline: e.target.value })}
                    placeholder="Qualify for tomorrow" className="h-11 rounded-xl border-[#E5DCD0] focus-visible:ring-[#FF6B35]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#8a7f76]">Logo (PNG/JPG, optional)</Label>
                  <label data-testid="onboarding-logo-upload"
                    className="flex items-center gap-4 rounded-xl border border-dashed border-[#E5DCD0] hover:border-[#FF6B35] px-4 py-4 cursor-pointer transition-colors">
                    {brand.logo_b64 ? (
                      <img src={brand.logo_b64} alt="Logo preview" className="h-12 w-12 object-contain rounded bg-[#FAF9F6] border border-[#F0EBE1]" />
                    ) : (
                      <span className="h-12 w-12 rounded-xl bg-[#FFD8C4]/40 flex items-center justify-center">
                        <UploadSimple size={18} className="text-[#FF6B35]" />
                      </span>
                    )}
                    <span className="text-sm text-[#8a7f76]">{brand.logo_b64 ? "Logo ready — click to replace" : "Click to upload your logo"}</span>
                    <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={onLogo} />
                  </label>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#8a7f76]">Brand colors</Label>
                  <div className="flex gap-3">
                    {brand.colors.map((c, i) => (
                      <label key={i} className="flex items-center gap-2 rounded-xl border border-[#F0EBE1] bg-white px-3 py-2 cursor-pointer">
                        <input type="color" data-testid={`onboarding-color-${i}`} value={c}
                          onChange={(e) => setBrand({ ...brand, colors: brand.colors.map((x, j) => (j === i ? e.target.value : x)) })}
                          className="h-6 w-6 rounded cursor-pointer border-0 bg-transparent p-0" />
                        <span className="text-xs font-mono text-[#8a7f76]">{c}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-8 flex items-center gap-4">
                <Button onClick={saveBrand} disabled={busy || !brand.brand_name.trim()} data-testid="onboarding-brand-save"
                  className="rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold px-8 h-11 transition-transform hover:-translate-y-0.5 active:scale-[0.98] shadow-[0_8px_30px_rgba(255,107,53,0.2)]">
                  {busy ? "Saving…" : "Save & continue"} <ArrowRight size={15} className="ml-1.5" />
                </Button>
                <button onClick={() => setStep(2)} data-testid="onboarding-brand-skip" className="text-sm font-bold text-[#b3a99e] hover:text-[#2D2422] transition-colors">
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="bg-white border border-[#F0EBE1] rounded-3xl p-8 shadow-[0_8px_30px_rgba(255,107,53,0.06)]" data-testid="onboarding-step-meta">
              <span className="h-12 w-12 rounded-2xl bg-[#0052FF]/10 flex items-center justify-center">
                <PlugsConnected size={22} weight="duotone" className="text-[#0052FF]" />
              </span>
              <h1 className="font-outfit text-2xl font-extrabold tracking-tight text-[#2D2422] mt-5">Connect your Meta ad accounts</h1>
              <p className="text-sm text-[#8a7f76] mt-2 leading-relaxed">
                5ads.ai syncs your live campaigns, ads, audiences and leads — and your AI team starts analyzing right away.
              </p>
              <div className="mt-8">
                {connected ? (
                  <div className="rounded-xl border border-[#1a9e52]/30 bg-[#1a9e52]/5 px-5 py-4 flex items-center gap-3" data-testid="onboarding-meta-connected">
                    <CheckCircle size={22} weight="fill" className="text-[#1a9e52]" />
                    <div>
                      <div className="text-sm font-bold text-[#2D2422]">Meta is connected</div>
                      <div className="text-xs text-[#8a7f76]">{metaStatus.account_count || (metaStatus.available_accounts || []).length} ad account(s) linked. You can fine-tune the selection on the Connect page anytime.</div>
                    </div>
                  </div>
                ) : oauthCfg?.available ? (
                  <Button onClick={startOauth} disabled={busy} data-testid="onboarding-fb-connect"
                    className="w-full h-12 rounded-full bg-[#1877F2] hover:bg-[#145fc4] text-white font-bold text-sm transition-transform hover:-translate-y-0.5 active:scale-[0.98]">
                    <FacebookLogo size={20} weight="fill" className="mr-2" /> {busy ? "Opening Facebook…" : "Continue with Facebook"}
                  </Button>
                ) : (
                  <div className="rounded-xl border border-[#F0EBE1] bg-[#FAF9F6] px-5 py-4 text-sm text-[#8a7f76] leading-relaxed" data-testid="onboarding-meta-manual-note">
                    One-click Facebook connect isn't configured yet. You can connect manually with your Meta App credentials
                    from the <span className="font-bold text-[#2D2422]">Connect Meta</span> page inside the app — it takes about 5 minutes.
                  </div>
                )}
              </div>
              <div className="mt-8 flex items-center gap-4">
                <Button onClick={() => setStep(3)} data-testid="onboarding-meta-continue"
                  className="rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold px-8 h-11 transition-transform hover:-translate-y-0.5 active:scale-[0.98] shadow-[0_8px_30px_rgba(255,107,53,0.2)]">
                  Continue <ArrowRight size={15} className="ml-1.5" />
                </Button>
                {!connected && (
                  <button onClick={() => setStep(3)} data-testid="onboarding-meta-skip" className="text-sm font-bold text-[#b3a99e] hover:text-[#2D2422] transition-colors">
                    I'll connect later
                  </button>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="bg-white border border-[#F0EBE1] rounded-3xl p-8 shadow-[0_8px_30px_rgba(255,107,53,0.06)]" data-testid="onboarding-step-team">
              <span className="h-12 w-12 rounded-2xl bg-[#FFD8C4]/50 flex items-center justify-center">
                <UsersThree size={22} weight="duotone" className="text-[#FF6B35]" />
              </span>
              <h1 className="font-outfit text-2xl font-extrabold tracking-tight text-[#2D2422] mt-5">Meet your AI team</h1>
              <p className="text-sm text-[#8a7f76] mt-2 leading-relaxed">
                Three teammates run your creative pipeline. Everything they make waits for your approval — nothing ships without you.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  { icon: PaintBrushBroad, name: "Riya", role: "Designer", d: "Generates ad images in your brand style, stamped with your real logo." },
                  { icon: Megaphone, name: "Arjun", role: "Ads Manager", d: "Writes copy, picks ad sets and sets budgets within ±20% guardrails." },
                  { icon: ShieldCheck, name: "Meera", role: "Senior Reviewer", d: "Reviews every ad against your live data before it reaches you." },
                ].map(({ icon: Icon, name, role, d }) => (
                  <div key={name} className="flex gap-4 rounded-xl border border-[#F0EBE1] px-5 py-4 transition-colors hover:border-[#FFD8C4]" data-testid={`onboarding-teammate-${name.toLowerCase()}`}>
                    <span className="h-11 w-11 shrink-0 rounded-xl bg-[#FFD8C4]/40 flex items-center justify-center">
                      <Icon size={20} weight="duotone" className="text-[#FF6B35]" />
                    </span>
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-outfit text-sm font-extrabold text-[#2D2422]">{name}</span>
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF6B35]">{role}</span>
                      </div>
                      <p className="text-xs text-[#8a7f76] leading-relaxed mt-0.5">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Button onClick={() => finish(false)} disabled={busy} data-testid="onboarding-finish"
                className="mt-8 w-full h-12 rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold text-sm transition-transform hover:-translate-y-0.5 active:scale-[0.98] shadow-[0_12px_40px_rgba(255,107,53,0.3)]">
                {busy ? "Initializing…" : "Enter your dashboard"} <ArrowRight size={15} className="ml-1.5" />
              </Button>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
